import type {
  AdminDropDto,
  AdminDropEntryDto,
  DropEligibilityRuleInput,
  DropEntryState,
  DropRuleType,
} from '@pulsify/shared'
import { Gift, ImagePlus, Link2, Paperclip, Pencil, Plus, Send, Trash2, X } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  useAdminDropEntries,
  useAdminDrops,
  useArchiveDrop,
  useCreateDrop,
  useDrawDrop,
  useDropLinkSearch,
  useMessageDropWinner,
  useMessageDropWinners,
  usePublishDrop,
  useUpdateDrop,
  useUploadDropHero,
} from '~/entities/drop/model/use-drops'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { extractApiError } from '~/shared/lib/api-errors'
import { cn } from '~/shared/lib/utils'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/shared/ui/alert-dialog'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card, CardContent } from '~/shared/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '~/shared/ui/dialog'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { SegmentedControl } from '~/shared/ui/segmented-control'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/shared/ui/select'
import { Textarea } from '~/shared/ui/textarea'
import { DailyDropPanel } from './daily-drop-panel'
import {
  type DropFormProblem,
  type DropFormState,
  type DropLinkKind,
  defaultDropFormState,
  dropCreateRequest,
  dropFormProblems,
  dropFormStateFromDrop,
  dropLinkage,
  liveDropUpdate,
} from './drop-form'

const STATUS_LABELS: Record<AdminDropDto['status'], () => string> = {
  draft: m.dropStatusDraft,
  live: m.dropStatusLive,
  drawn: m.dropStatusDrawn,
  archived: m.dropStatusArchived,
}

const STATUS_VARIANTS: Record<AdminDropDto['status'], 'secondary' | 'default' | 'outline'> = {
  draft: 'secondary',
  live: 'default',
  drawn: 'outline',
  archived: 'outline',
}

const ENTRY_STATE_LABELS: Record<DropEntryState, () => string> = {
  none: m.dropEntryStateLost,
  entered: m.dropEntryStateEntered,
  winner_pending: m.dropEntryStateWinnerPending,
  claimed: m.dropEntryStateClaimed,
  forfeited: m.dropEntryStateForfeited,
  lost: m.dropEntryStateLost,
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(getLocale(), { dateStyle: 'medium', timeStyle: 'short' })
}

const RULE_TYPE_LABELS: Record<DropRuleType, () => string> = {
  interested_in_event: m.dropRuleTypeEvent,
  interested_in_listing: m.dropRuleTypeListing,
  invite_friends: m.dropRuleTypeInvite,
}

const PROBLEM_MESSAGES: Record<DropFormProblem, () => string> = {
  titleRequired: m.dropValidationTitleRequired,
  prizeNameRequired: m.dropValidationPrizeNameRequired,
  termsRequired: m.dropValidationTermsRequired,
  prizeValueFormat: m.dropValidationPrizeValueFormat,
  drawAfterStart: m.dropValidationDrawAfterStart,
  contingentRequired: m.dropValidationContingentRequired,
  contingentOnlyGrows: m.dropValidationContingentOnlyGrows,
  winsPerDayRequired: m.dropValidationWinsPerDayRequired,
  placeRequired: m.dropValidationPlaceRequired,
}

function DropFormDialog({
  open,
  editing,
  onClose,
}: {
  open: boolean
  editing: AdminDropDto | null
  onClose: () => void
}) {
  const [form, setForm] = useState<DropFormState>(() => defaultDropFormState(new Date()))
  const [linkName, setLinkName] = useState('')
  const [linkSearch, setLinkSearch] = useState('')
  const [debouncedLinkSearch, setDebouncedLinkSearch] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false)
  const initialFormRef = useRef<DropFormState>(form)
  const createDrop = useCreateDrop()
  const updateDrop = useUpdateDrop()
  const isLive = editing?.status === 'live'
  const pending = createDrop.isPending || updateDrop.isPending

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedLinkSearch(linkSearch), 300)
    return () => clearTimeout(timer)
  }, [linkSearch])

  const linkSearchQuery = useDropLinkSearch(
    form.linkedId ? 'none' : form.linkKind,
    debouncedLinkSearch,
  )

  const isDirty = JSON.stringify(form) !== JSON.stringify(initialFormRef.current)

  const resetForm = () => {
    const initial = editing ? dropFormStateFromDrop(editing) : defaultDropFormState(new Date())
    setForm(initial)
    initialFormRef.current = initial
    setLinkName(editing?.linkedEntityName ?? '')
    setLinkSearch('')
    setDebouncedLinkSearch('')
    setErrors({})
  }

  const set = <K extends keyof DropFormState>(key: K, value: DropFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => {
      if (!(key in prev)) return prev
      const next = { ...prev }
      delete next[key as string]
      return next
    })
  }

  const setLinkKind = (kind: DropLinkKind) => {
    setForm((prev) => ({ ...prev, linkKind: kind, linkedId: '' }))
    setLinkName('')
    setLinkSearch('')
  }

  const linkage = dropLinkage(form)
  const isDaily = form.kind === 'daily'
  const fieldIds = useId()
  const kindLocked = !!editing && editing.status !== 'draft'

  const setKind = (kind: DropFormState['kind']) => {
    setForm((prev) =>
      kind === 'daily' && prev.linkKind !== 'listing'
        ? { ...prev, kind, linkKind: 'listing', linkedId: '' }
        : { ...prev, kind },
    )
    if (kind === 'daily' && form.linkKind !== 'listing') {
      setLinkName('')
      setLinkSearch('')
    }
    setErrors({})
  }

  const hasLinkedEvent = !!linkage.linkedEventId
  const hasLinkedListing = !!linkage.linkedListingId

  const addRule = () => {
    const rule: DropEligibilityRuleInput = {
      type: hasLinkedEvent ? 'interested_in_event' : 'interested_in_listing',
      config: {},
    }
    setForm((prev) => ({ ...prev, rules: [...prev.rules, rule] }))
  }

  const setRuleType = (index: number, type: DropRuleType) => {
    setForm((prev) => ({
      ...prev,
      rules: prev.rules.map((rule, i) => (i === index ? { type, config: {} } : rule)),
    }))
  }

  const removeRule = (index: number) => {
    setForm((prev) => ({ ...prev, rules: prev.rules.filter((_, i) => i !== index) }))
  }

  const ruleNeedsMissingLink = (rule: DropEligibilityRuleInput) =>
    (rule.type === 'interested_in_event' && !hasLinkedEvent) ||
    (rule.type === 'interested_in_listing' && !hasLinkedListing)

  const attemptClose = () => {
    if (isDirty) setConfirmDiscardOpen(true)
    else onClose()
  }

  const validate = (): Record<string, string> =>
    Object.fromEntries(
      Object.entries(dropFormProblems(form, { live: isLive ? editing : null })).map(
        ([field, problem]) => [field, PROBLEM_MESSAGES[problem]()],
      ),
    )

  const onMutationError = (error: unknown) => {
    const apiError = extractApiError(error)
    if (apiError.errorCode === 'ValidationFailed' && form.kind === 'draw') {
      setErrors((prev) => ({ ...prev, drawAt: m.dropValidationDrawAfterStart() }))
    }
  }

  const submit = () => {
    const validation = validate()
    if (Object.keys(validation).length > 0) {
      setErrors(validation)
      return
    }
    setErrors({})
    const options = { onSuccess: () => onClose(), onError: onMutationError }
    if (editing && isLive) {
      updateDrop.mutate({ id: editing.id, input: liveDropUpdate(form) }, options)
      return
    }
    const input = dropCreateRequest(form)
    if (editing) {
      updateDrop.mutate({ id: editing.id, input }, options)
    } else {
      createDrop.mutate(input, options)
    }
  }

  const fieldError = (key: string) =>
    errors[key] ? <p className="text-destructive text-xs">{errors[key]}</p> : null

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) attemptClose()
        }}
      >
        <DialogContent
          className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
          onOpenAutoFocus={resetForm}
          onInteractOutside={(e) => {
            if (isDirty) {
              e.preventDefault()
              setConfirmDiscardOpen(true)
            }
          }}
          onEscapeKeyDown={(e) => {
            if (isDirty) {
              e.preventDefault()
              setConfirmDiscardOpen(true)
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>{editing ? m.dropEdit() : m.dropCreate()}</DialogTitle>
          </DialogHeader>
          <div className="gap-4 grid sm:grid-cols-2">
            {!kindLocked && (
              <div className="sm:col-span-2 space-y-2">
                <SegmentedControl
                  label={m.dropKindLabel()}
                  value={form.kind}
                  options={[
                    { value: 'draw', label: m.dropKindDraw() },
                    { value: 'daily', label: m.dropKindDaily() },
                  ]}
                  onChange={setKind}
                />
                {isDaily && <p className="text-muted-foreground text-xs">{m.dropDailyHint()}</p>}
              </div>
            )}
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="drop-title">{m.dropTitleLabel()}</Label>
              <Input
                id="drop-title"
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
              />
              {fieldError('title')}
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="drop-description">{m.dropDescriptionLabel()}</Label>
              <Textarea
                id="drop-description"
                rows={3}
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="drop-prize-name">{m.dropPrizeName()}</Label>
              <Input
                id="drop-prize-name"
                value={form.prizeName}
                disabled={isLive}
                onChange={(e) => set('prizeName', e.target.value)}
              />
              {fieldError('prizeName')}
            </div>
            <div className="space-y-2">
              <Label htmlFor="drop-prize-value">{m.dropPrizeValue()}</Label>
              <Input
                id="drop-prize-value"
                inputMode="decimal"
                placeholder="149.00"
                value={form.prizeValueEur}
                disabled={isLive}
                onChange={(e) => set('prizeValueEur', e.target.value)}
              />
              {fieldError('prizeValueEur')}
            </div>
            <div className="space-y-2">
              <Label htmlFor="drop-starts-at">{m.dropStartsAt()}</Label>
              <Input
                id="drop-starts-at"
                type="datetime-local"
                value={form.startsAt}
                disabled={isLive}
                onChange={(e) => set('startsAt', e.target.value)}
              />
            </div>
            {isDaily ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor={`${fieldIds}-contingent`}>{m.dropContingent()}</Label>
                  <Input
                    id={`${fieldIds}-contingent`}
                    type="number"
                    min={1}
                    max={10000}
                    value={form.prizeContingent}
                    onChange={(e) => set('prizeContingent', e.target.value)}
                  />
                  {fieldError('prizeContingent')}
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`${fieldIds}-wins-per-day`}>{m.dropWinsPerDay()}</Label>
                  <Input
                    id={`${fieldIds}-wins-per-day`}
                    type="number"
                    min={1}
                    max={1000}
                    value={form.winsPerDay}
                    disabled={isLive}
                    onChange={(e) => set('winsPerDay', e.target.value)}
                  />
                  {fieldError('winsPerDay')}
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`${fieldIds}-voucher-days`}>{m.dropVoucherValidDays()}</Label>
                  <Input
                    id={`${fieldIds}-voucher-days`}
                    type="number"
                    min={1}
                    max={90}
                    value={form.voucherValidDays}
                    disabled={isLive}
                    onChange={(e) => set('voucherValidDays', e.target.value)}
                  />
                </div>
                <div className="sm:col-span-2 space-y-2">
                  <Label htmlFor={`${fieldIds}-voucher-note`}>{m.dropVoucherNote()}</Label>
                  <Textarea
                    id={`${fieldIds}-voucher-note`}
                    rows={2}
                    maxLength={300}
                    placeholder={m.dropVoucherNotePlaceholder()}
                    value={form.voucherNote}
                    onChange={(e) => set('voucherNote', e.target.value)}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="drop-draw-at">{m.dropDrawAt()}</Label>
                  <Input
                    id="drop-draw-at"
                    type="datetime-local"
                    value={form.drawAt}
                    disabled={isLive}
                    onChange={(e) => set('drawAt', e.target.value)}
                  />
                  {fieldError('drawAt')}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="drop-winner-count">{m.dropWinnerCount()}</Label>
                  <Input
                    id="drop-winner-count"
                    type="number"
                    min={1}
                    max={100}
                    value={form.winnerCount}
                    disabled={isLive}
                    onChange={(e) => set('winnerCount', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="drop-claim-window">{m.dropClaimWindowDays()}</Label>
                  <Input
                    id="drop-claim-window"
                    type="number"
                    min={1}
                    max={60}
                    value={form.claimWindowDays}
                    disabled={isLive}
                    onChange={(e) => set('claimWindowDays', e.target.value)}
                  />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label>{m.dropMinAge()}</Label>
              <Select
                value={form.minAge}
                disabled={isLive}
                onValueChange={(value) => set('minAge', value === '16' ? '16' : '18')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="16">16+</SelectItem>
                  <SelectItem value="18">18+</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{m.dropParticipationLabel()}</Label>
              <Select
                value={form.participation}
                onValueChange={(value) => set('participation', value === 'web' ? 'web' : 'app')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="app">{m.dropParticipationApp()}</SelectItem>
                  <SelectItem value="web">{m.dropParticipationWeb()}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-xs">{m.dropParticipationHint()}</p>
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label>{m.dropLinkLabel()}</Label>
              <div className="flex gap-2">
                <Select
                  value={form.linkKind}
                  onValueChange={(value) => setLinkKind(value as DropLinkKind)}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {!isDaily && <SelectItem value="none">{m.dropLinkNone()}</SelectItem>}
                    {!isDaily && <SelectItem value="event">{m.dropLinkEvent()}</SelectItem>}
                    <SelectItem value="listing">{m.dropLinkListing()}</SelectItem>
                  </SelectContent>
                </Select>
                {form.linkKind !== 'none' && !form.linkedId && (
                  <Input
                    value={linkSearch}
                    placeholder={m.dropLinkSearchPlaceholder()}
                    onChange={(e) => setLinkSearch(e.target.value)}
                  />
                )}
                {form.linkKind !== 'none' && form.linkedId && (
                  <div className="flex flex-1 items-center gap-2 px-3 border border-slate-200 dark:border-slate-800 rounded-md min-w-0">
                    <Link2 className="w-4 h-4 text-slate-400 shrink-0" />
                    <span className="flex-1 text-slate-900 dark:text-slate-100 text-sm truncate">
                      {linkName}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-1"
                      title={m.dropLinkClear()}
                      onClick={() => {
                        setForm((prev) => ({ ...prev, linkedId: '' }))
                        setLinkName('')
                        setLinkSearch('')
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
              {form.linkKind !== 'none' &&
                !form.linkedId &&
                debouncedLinkSearch.trim().length >= 2 && (
                  <div className="border border-slate-200 dark:border-slate-800 rounded-md divide-y divide-slate-200 dark:divide-slate-800 overflow-hidden">
                    {(linkSearchQuery.data ?? []).map((target) => (
                      <button
                        key={target.id}
                        type="button"
                        className="block hover:bg-slate-50 dark:hover:bg-slate-900 px-3 py-2 w-full text-left text-slate-900 dark:text-slate-100 text-sm truncate"
                        onClick={() => {
                          setForm((prev) => ({ ...prev, linkedId: target.id }))
                          setLinkName(target.name)
                          setLinkSearch('')
                        }}
                      >
                        {target.name}
                      </button>
                    ))}
                    {!linkSearchQuery.isFetching && (linkSearchQuery.data ?? []).length === 0 && (
                      <p className="px-3 py-2 text-slate-500 dark:text-slate-400 text-sm">
                        {m.dropLinkNoResults()}
                      </p>
                    )}
                  </div>
                )}
              {isDaily && <p className="text-muted-foreground text-xs">{m.dropPlaceHint()}</p>}
              {fieldError('linkedId')}
            </div>
            <div className="sm:col-span-2 space-y-3">
              <div className="flex justify-between items-center">
                <Label>{m.dropRulesLabel()}</Label>
                {!isLive && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={form.rules.length >= 10 || (!hasLinkedEvent && !hasLinkedListing)}
                    onClick={addRule}
                  >
                    <Plus className="mr-1 w-4 h-4" />
                    {m.dropRuleAdd()}
                  </Button>
                )}
              </div>
              <p className="text-slate-500 dark:text-slate-400 text-xs">{m.dropRulesHint()}</p>
              {form.rules.length === 0 ? (
                <p className="text-slate-500 dark:text-slate-400 text-sm">{m.dropRulesEmpty()}</p>
              ) : (
                <div className="space-y-2">
                  {form.rules.map((rule, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: rules are a small positional list
                    <div key={index} className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Select
                          value={rule.type}
                          disabled={isLive}
                          onValueChange={(value) => setRuleType(index, value as DropRuleType)}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="interested_in_event" disabled={!hasLinkedEvent}>
                              {RULE_TYPE_LABELS.interested_in_event()}
                            </SelectItem>
                            <SelectItem value="interested_in_listing" disabled={!hasLinkedListing}>
                              {RULE_TYPE_LABELS.interested_in_listing()}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        {!isLive && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="px-2 text-destructive hover:text-destructive"
                            title={m.dropRuleRemove()}
                            onClick={() => removeRule(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      {ruleNeedsMissingLink(rule) && (
                        <p className="text-amber-600 dark:text-amber-500 text-xs">
                          {m.dropRuleNeedsLink()}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {isLive && (
                <p className="text-slate-500 dark:text-slate-400 text-xs">{m.dropRulesLocked()}</p>
              )}
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="drop-terms">{m.dropTerms()}</Label>
              <Textarea
                id="drop-terms"
                rows={8}
                value={form.terms}
                onChange={(e) => set('terms', e.target.value)}
              />
              {fieldError('terms')}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={attemptClose}>
              {m.cancel()}
            </Button>
            <Button
              onClick={submit}
              disabled={pending}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {m.save()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={confirmDiscardOpen} onOpenChange={setConfirmDiscardOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{m.dropDiscardTitle()}</AlertDialogTitle>
            <AlertDialogDescription>{m.dropDiscardBody()}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{m.cancel()}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmDiscardOpen(false)
                onClose()
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {m.dropDiscardConfirm()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

const WINNER_FILE_MAX_BYTES = 25 * 1024 * 1024

function WinnersMessageCard({ dropId, winners }: { dropId: string; winners: AdminDropEntryDto[] }) {
  const [message, setMessage] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const attachInputRef = useRef<HTMLInputElement>(null)
  const sendWinners = useMessageDropWinners()

  const sentCount = winners.filter((w) => w.winnerMessageSentAt !== null).length
  const isResend = sentCount === winners.length
  const targetCount = isResend ? winners.length : winners.length - sentCount
  const lastSentAt = winners.reduce<string | null>(
    (latest, w) =>
      w.winnerMessageSentAt && (!latest || w.winnerMessageSentAt > latest)
        ? w.winnerMessageSentAt
        : latest,
    null,
  )

  const send = () => {
    sendWinners.mutate(
      { id: dropId, message: message.trim(), file, resend: isResend },
      {
        onSuccess: () => {
          setMessage('')
          setFile(null)
        },
      },
    )
  }

  return (
    <div className="space-y-3 p-4 border border-slate-200 dark:border-slate-800 rounded-lg">
      <h3 className="font-semibold text-slate-900 dark:text-slate-100">
        {m.dropWinnersMessageTitle()}
      </h3>
      <p className="text-slate-500 dark:text-slate-400 text-xs">{m.dropWinnersMessageHint()}</p>
      {sentCount > 0 && lastSentAt && (
        <p className="text-slate-600 dark:text-slate-400 text-sm">
          {m.dropWinnersSentState({
            sent: sentCount,
            total: winners.length,
            date: formatDateTime(lastSentAt),
          })}
        </p>
      )}
      <Textarea
        rows={4}
        placeholder={m.dropMessagePlaceholder()}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
      />
      <input
        ref={attachInputRef}
        type="file"
        accept="application/pdf,image/*"
        className="hidden"
        onChange={(e) => {
          const chosen = e.target.files?.[0]
          if (chosen) {
            if (chosen.size > WINNER_FILE_MAX_BYTES) toast.error(m.dropWinnersFileTooLarge())
            else setFile(chosen)
          }
          e.target.value = ''
        }}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={sendWinners.isPending}
          onClick={() => attachInputRef.current?.click()}
        >
          <Paperclip className="mr-1 w-4 h-4" />
          {m.dropWinnersAttach()}
        </Button>
        {file && (
          <div className="flex items-center gap-2 px-3 py-1 border border-slate-200 dark:border-slate-800 rounded-md min-w-0">
            <span className="text-slate-900 dark:text-slate-100 text-sm truncate">{file.name}</span>
            <Button
              variant="ghost"
              size="sm"
              className="px-1"
              title={m.dropWinnersAttachRemove()}
              onClick={() => setFile(null)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}
        <Button
          size="sm"
          disabled={sendWinners.isPending || message.trim().length === 0}
          onClick={() => setConfirmOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Send className="mr-1 w-4 h-4" />
          {isResend && sentCount > 0 ? m.dropWinnersResend() : m.dropWinnersSend()}
        </Button>
      </div>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{m.dropWinnersConfirmTitle({ count: targetCount })}</AlertDialogTitle>
            <AlertDialogDescription>
              {isResend && sentCount > 0
                ? m.dropWinnersResendConfirmBody({ count: targetCount })
                : m.dropWinnersConfirmBody()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{m.cancel()}</AlertDialogCancel>
            <AlertDialogAction onClick={send} className="bg-blue-600 hover:bg-blue-700 text-white">
              {isResend && sentCount > 0 ? m.dropWinnersResend() : m.dropWinnersSend()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function WinnerMessageBox({ dropId, entry }: { dropId: string; entry: AdminDropEntryDto }) {
  const [message, setMessage] = useState('')
  const sendMessage = useMessageDropWinner()

  return (
    <div className="space-y-2 mt-2">
      <Textarea
        rows={2}
        placeholder={m.dropMessagePlaceholder()}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
      />
      <Button
        size="sm"
        variant="outline"
        disabled={sendMessage.isPending || message.trim().length === 0}
        onClick={() =>
          sendMessage.mutate(
            { id: dropId, userId: entry.userId, message: message.trim() },
            { onSuccess: () => setMessage('') },
          )
        }
      >
        <Send className="mr-1 w-4 h-4" />
        {m.dropMessageSend()}
      </Button>
    </div>
  )
}

function DropDetailPanel({ drop }: { drop: AdminDropDto }) {
  const isDaily = drop.kind === 'daily'
  const { data: entriesData } = useAdminDropEntries(isDaily ? null : drop.id)
  const publishDrop = usePublishDrop()
  const archiveDrop = useArchiveDrop()
  const drawDrop = useDrawDrop()
  const uploadHero = useUploadDropHero()
  const [drawConfirmOpen, setDrawConfirmOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const drawDue = drop.drawAt !== null && new Date(drop.drawAt).getTime() <= Date.now()
  const entries = entriesData?.entries ?? []
  const winners = entries.filter((e) => e.wonAt !== null)
  const activeWinners = winners.filter((e) => e.forfeitedAt === null)

  return (
    <Card className="mt-6">
      <CardContent className="space-y-6 p-6">
        <div className="flex sm:flex-row flex-col sm:items-start gap-4">
          {drop.heroImageUrl ? (
            <img
              src={drop.heroImageUrl}
              alt={drop.title}
              className="rounded-lg w-full sm:w-48 h-28 object-cover"
            />
          ) : (
            <div className="flex justify-center items-center bg-slate-100 dark:bg-slate-800 rounded-lg w-full sm:w-48 h-28">
              <Gift className="w-8 h-8 text-slate-400" />
            </div>
          )}
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-2">
              <h2 className="font-bold text-slate-900 dark:text-slate-100 text-xl">{drop.title}</h2>
              <Badge variant={STATUS_VARIANTS[drop.status]}>{STATUS_LABELS[drop.status]()}</Badge>
            </div>
            <p className="text-slate-600 dark:text-slate-400 text-sm">
              {drop.prizeName}
              {Number(drop.prizeValueEur) > 0 && ` · ${drop.prizeValueEur} EUR`} · {drop.minAge}+
            </p>
            <p className="text-slate-600 dark:text-slate-400 text-sm">
              {drop.drawAt && `${m.dropDrawAt()}: ${formatDateTime(drop.drawAt)} · `}
              {m.dropParticipants()}: {drop.participantCount}
            </p>
            {isDaily && (
              <p className="text-slate-600 dark:text-slate-400 text-sm">
                {m.dropKindDaily()} ·{' '}
                {m.dropDailyRemaining({
                  remaining: Math.max(0, (drop.prizeContingent ?? 0) - drop.prizesWon),
                  contingent: drop.prizeContingent ?? 0,
                })}{' '}
                ·{' '}
                {m.dropDailySummary({
                  winsPerDay: drop.winsPerDay ?? 0,
                  days: drop.voucherValidDays ?? 0,
                })}
              </p>
            )}
            {drop.linkedEntityName && (
              <p className="flex items-center gap-1 text-slate-600 dark:text-slate-400 text-sm">
                <Link2 className="w-3.5 h-3.5" />
                {m.dropLinkLabel()}: {drop.linkedEventId ? m.dropLinkEvent() : m.dropLinkListing()}{' '}
                · {drop.linkedEntityName}
              </p>
            )}
            {drop.drawSeed && (
              <p className="text-slate-500 dark:text-slate-500 text-xs break-all">
                {m.dropDrawSeed()}: {drop.drawSeed}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) uploadHero.mutate({ id: drop.id, file })
              e.target.value = ''
            }}
          />
          <Button
            variant="outline"
            size="sm"
            disabled={uploadHero.isPending || drop.status === 'archived'}
            onClick={() => fileInputRef.current?.click()}
          >
            <ImagePlus className="mr-1 w-4 h-4" />
            {m.dropHeroUpload()}
          </Button>
          {drop.status === 'draft' && (
            <Button
              size="sm"
              disabled={publishDrop.isPending}
              onClick={() => publishDrop.mutate(drop.id)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {m.dropPublish()}
            </Button>
          )}
          {drop.status === 'live' && !isDaily && (
            <Button
              size="sm"
              disabled={drawDrop.isPending || !drawDue}
              title={drawDue ? undefined : m.dropDrawNotDue()}
              onClick={() => setDrawConfirmOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {m.dropDraw()}
            </Button>
          )}
          {drop.status !== 'archived' && (
            <Button
              variant="outline"
              size="sm"
              disabled={archiveDrop.isPending}
              onClick={() => archiveDrop.mutate(drop.id)}
              className="text-destructive hover:text-destructive"
            >
              {m.dropArchive()}
            </Button>
          )}
        </div>

        {isDaily && drop.status !== 'draft' && <DailyDropPanel drop={drop} />}

        {!isDaily && (
          <div>
            <h3 className="mb-2 font-semibold text-slate-900 dark:text-slate-100">
              {m.dropEntriesTitle()} ({entriesData?.participantCount ?? drop.participantCount})
            </h3>
            {entries.length === 0 ? (
              <p className="text-slate-600 dark:text-slate-400 text-sm">-</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-slate-200 dark:border-slate-800 border-b text-left text-slate-500 dark:text-slate-400">
                      <th className="px-2 py-2 font-medium">{m.dropRank()}</th>
                      <th className="px-2 py-2 font-medium">{m.dropUserCol()}</th>
                      <th className="px-2 py-2 font-medium">{m.dropStateCol()}</th>
                      <th className="px-2 py-2 font-medium">{m.details()}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {entries.map((entry) => (
                      <tr key={entry.userId} className="align-top">
                        <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                          {entry.drawRank ?? '-'}
                        </td>
                        <td className="px-2 py-2 text-slate-900 dark:text-slate-100">
                          {entry.displayName ?? entry.userId}
                        </td>
                        <td className="px-2 py-2">
                          <Badge variant={entry.wonAt ? 'default' : 'outline'}>
                            {ENTRY_STATE_LABELS[entry.state]()}
                          </Badge>
                        </td>
                        <td className="px-2 py-2 text-slate-500 dark:text-slate-400 text-xs whitespace-nowrap">
                          <div>{formatDateTime(entry.createdAt)}</div>
                          {entry.claimedAt && <div>✓ {formatDateTime(entry.claimedAt)}</div>}
                          {entry.remindedAt && !entry.claimedAt && (
                            <div>⏰ {formatDateTime(entry.remindedAt)}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeWinners.length > 0 && (
          <WinnersMessageCard dropId={drop.id} winners={activeWinners} />
        )}

        {winners.length > 0 && (
          <div>
            <h3 className="mb-2 font-semibold text-slate-900 dark:text-slate-100">
              {m.dropMessageWinner()}
            </h3>
            <div className="space-y-4">
              {winners.map((winner) => (
                <div
                  key={winner.userId}
                  className="p-3 border border-slate-200 dark:border-slate-800 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 dark:text-slate-100 text-sm">
                      {winner.displayName ?? winner.userId}
                    </span>
                    <Badge variant="outline">{ENTRY_STATE_LABELS[winner.state]()}</Badge>
                  </div>
                  <WinnerMessageBox dropId={drop.id} entry={winner} />
                </div>
              ))}
            </div>
          </div>
        )}

        <AlertDialog open={drawConfirmOpen} onOpenChange={setDrawConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{m.dropDrawConfirmTitle()}</AlertDialogTitle>
              <AlertDialogDescription>{m.dropDrawConfirmBody()}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{m.cancel()}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => drawDrop.mutate(drop.id)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {m.dropDraw()}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  )
}

export function DropsPage() {
  const { data: drops, isLoading, error } = useAdminDrops()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<AdminDropDto | null>(null)

  const selected = drops?.find((d) => d.id === selectedId) ?? null

  return (
    <div className="p-4 py-8 sm:p-8 sm:py-24">
      <div className="mx-auto max-w-5xl">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="mb-2 font-bold text-slate-900 dark:text-slate-100 text-3xl">
              {m.drops()}
            </h1>
            <p className="text-slate-600 dark:text-slate-400">{m.dropsDescription()}</p>
          </div>
          <Button
            onClick={() => {
              setEditing(null)
              setFormOpen(true)
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="mr-1 w-4 h-4" />
            {m.dropCreate()}
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <div className="border-blue-600 border-t-4 rounded-full w-12 h-12 animate-spin" />
          </div>
        ) : error ? (
          <Card>
            <CardContent className="py-8">
              <p className="text-sm text-destructive">{m.dropLoadError()}</p>
            </CardContent>
          </Card>
        ) : !drops || drops.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-4 py-16">
              <Gift className="w-12 h-12 text-slate-400" />
              <p className="text-slate-600 dark:text-slate-400 text-sm">{m.noDrops()}</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-slate-200 dark:border-slate-800 border-b text-left text-slate-500 dark:text-slate-400">
                    <th className="px-4 py-3 font-medium">{m.dropTitleLabel()}</th>
                    <th className="px-4 py-3 font-medium">{m.dropStateCol()}</th>
                    <th className="px-4 py-3 font-medium">{m.dropPrizeName()}</th>
                    <th className="px-4 py-3 font-medium">{m.dropDrawAt()}</th>
                    <th className="px-4 py-3 font-medium">{m.dropParticipants()}</th>
                    <th className="px-4 py-3 font-medium text-right">
                      {m.contentReportsActions()}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {drops.map((drop) => (
                    <tr
                      key={drop.id}
                      className={cn(
                        'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900',
                        selectedId === drop.id && 'bg-blue-50 dark:bg-blue-950',
                      )}
                      onClick={() => setSelectedId(drop.id === selectedId ? null : drop.id)}
                    >
                      <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                        {drop.title}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={STATUS_VARIANTS[drop.status]}>
                          {STATUS_LABELS[drop.status]()}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                        {drop.prizeName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-slate-600 dark:text-slate-400">
                        {drop.drawAt ? formatDateTime(drop.drawAt) : m.dropDailyWhen()}
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                        {drop.participantCount}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {(drop.status === 'draft' || drop.status === 'live') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              setEditing(drop)
                              setFormOpen(true)
                            }}
                          >
                            <Pencil className="mr-1 w-4 h-4" />
                            {m.dropEdit()}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {selected && <DropDetailPanel drop={selected} />}

        <DropFormDialog open={formOpen} editing={editing} onClose={() => setFormOpen(false)} />
      </div>
    </div>
  )
}
